import React, { useEffect, useMemo, useState } from 'react'
import {
  getSkills,
  getApplicationSkills,
  addApplicationSkill,
  updateApplicationSkill,
  removeApplicationSkill,
} from '@/api/tapResources'
import {
  TapSkill,
  TapApplicationSkill,
  TapSkillProficiency,
  TAP_SKILL_PROFICIENCIES,
} from '@/types/index'
import { Plus, X } from 'lucide-react'

// Feature #153 — Skills panel.
// Loads the global skill library once, plus the application's existing skill
// associations. Lets the user add a new skill (optionally with a proficiency),
// change proficiency inline, and remove (soft-delete) an association.
interface SkillsPanelProps {
  applicationId: string
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  backgroundColor: 'var(--color-white)',
}

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '999px',
  fontSize: '13px',
  backgroundColor: 'var(--color-white)',
}

const SkillsPanel: React.FC<SkillsPanelProps> = ({ applicationId }) => {
  const [library, setLibrary] = useState<TapSkill[]>([])
  const [assocs, setAssocs] = useState<TapApplicationSkill[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newSkillId, setNewSkillId] = useState('')
  const [newProficiency, setNewProficiency] = useState<TapSkillProficiency | ''>('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [libResult, assocsResult] = await Promise.all([
        getSkills(),
        getApplicationSkills(applicationId),
      ])
      setLibrary(libResult)
      setAssocs(assocsResult)
    } catch {
      setError('Unable to load skills')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Skills already attached to this application shouldn't appear in the "add"
  // dropdown — the backend returns 409 for duplicates, and it's nicer to hide
  // the option than to surface an error.
  const availableToAdd = useMemo(() => {
    const assignedIds = new Set(assocs.filter((a) => a.isActive).map((a) => a.skillId))
    return library.filter((s) => s.isActive && !assignedIds.has(s.id))
  }, [library, assocs])

  const handleAdd = async () => {
    if (!newSkillId) return
    setBusy(true)
    try {
      await addApplicationSkill(applicationId, newSkillId, newProficiency || undefined)
      setNewSkillId('')
      setNewProficiency('')
      await load()
    } catch {
      alert('Failed to add skill')
    } finally {
      setBusy(false)
    }
  }

  const handleProficiencyChange = async (assoc: TapApplicationSkill, value: string) => {
    setBusy(true)
    try {
      await updateApplicationSkill(applicationId, assoc.id, value || undefined)
      await load()
    } catch {
      alert('Failed to update proficiency')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (assoc: TapApplicationSkill) => {
    if (!confirm(`Remove ${assoc.skillName || 'skill'} from this application?`)) return
    setBusy(true)
    try {
      await removeApplicationSkill(applicationId, assoc.id)
      await load()
    } catch {
      alert('Failed to remove skill')
    } finally {
      setBusy(false)
    }
  }

  const activeAssocs = assocs.filter((a) => a.isActive)

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
        Skills
      </h3>

      {loading && (
        <div style={{ fontSize: '14px', color: 'var(--color-dark-grey)' }}>Loading skills…</div>
      )}

      {!loading && error && (
        <div style={{ fontSize: '14px', color: 'var(--color-magenta)' }}>{error}</div>
      )}

      {!loading && !error && (
        <>
          {activeAssocs.length === 0 ? (
            <div style={{ fontSize: '14px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
              No skills captured yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {activeAssocs.map((assoc) => (
                <span key={assoc.id} style={pillStyle}>
                  <strong>{assoc.skillName || assoc.skillId}</strong>
                  <select
                    value={(assoc.proficiency as string) || ''}
                    onChange={(e) => handleProficiencyChange(assoc, e.target.value)}
                    disabled={busy}
                    style={{
                      ...inputStyle,
                      padding: '2px 6px',
                      fontSize: '12px',
                      backgroundColor: 'transparent',
                      border: '1px solid var(--color-light-grey)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">— level —</option>
                    {TAP_SKILL_PROFICIENCIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemove(assoc)}
                    disabled={busy}
                    aria-label="Remove skill"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--color-dark-grey)',
                      padding: '2px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={newSkillId}
              onChange={(e) => setNewSkillId(e.target.value)}
              disabled={busy}
              style={{ ...inputStyle, minWidth: '220px', cursor: 'pointer' }}
            >
              <option value="">Select a skill…</option>
              {availableToAdd.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select
              value={newProficiency}
              onChange={(e) => setNewProficiency(e.target.value as TapSkillProficiency | '')}
              disabled={busy || !newSkillId}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">Proficiency…</option>
              {TAP_SKILL_PROFICIENCIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <button
              onClick={handleAdd}
              disabled={busy || !newSkillId}
              style={{
                padding: '8px 14px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: busy || !newSkillId ? 'not-allowed' : 'pointer',
                opacity: busy || !newSkillId ? 0.6 : 1,
                fontFamily: 'var(--font-family)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {availableToAdd.length === 0 && library.length > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '8px' }}>
              All library skills are already attached to this application.
            </div>
          )}
          {library.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '8px' }}>
              The skills library is empty. Add skills via Settings.
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SkillsPanel
